import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { AnalyticsFilters } from '../types/analytics.types';
import { useTopUsersAnalytics, useUserDetailAnalytics, useDailySessionAnalytics } from '../hooks/useAnalytics';
import { TopUsersTable } from './TopUsersTable';
import { UserDetailModal } from './UserDetailModal';
import './UsersTab.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface UsersTabProps {
  filters: AnalyticsFilters;
}

type ViewMode = 'overview' | 'users';

export const UsersTab: React.FC<UsersTabProps> = ({ filters }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch daily session stats for overview charts
  const { data: dailyData, loading: dailyLoading } = useDailySessionAnalytics(filters);

  // Fetch top users data (increased limit to get all users for client-side pagination)
  const { data: topUsersData, loading: topUsersLoading, error: topUsersError } = useTopUsersAnalytics(filters, 1000);

  // Fetch individual user detail when user is selected
  const { data: userDetailData, loading: userDetailLoading } = useUserDetailAnalytics(selectedUserId, filters);

  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedUserId(null), 300);
  };

  // Prepare chart data for sessions
  const sessionChartData = dailyData ? {
    labels: dailyData.daily_stats.map(d => d.date),
    datasets: [
      {
        label: 'Sessions',
        data: dailyData.daily_stats.map(d => d.session_count),
        borderColor: '#4A90E2',
        backgroundColor: 'rgba(74, 144, 226, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#4A90E2',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  } : null;

  // Prepare chart data for average duration
  const durationChartData = dailyData ? {
    labels: dailyData.daily_stats.map(d => d.date),
    datasets: [
      {
        label: 'Avg Duration (min)',
        data: dailyData.daily_stats.map(d => d.avg_duration_minutes),
        borderColor: '#50C878',
        backgroundColor: 'rgba(80, 200, 120, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#50C878',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  } : null;

  // Prepare chart data for unique users
  const uniqueUsersChartData = dailyData ? {
    labels: dailyData.daily_stats.map(d => d.date),
    datasets: [
      {
        label: 'Unique Users',
        data: dailyData.daily_stats.map(d => d.unique_users),
        borderColor: '#9B59B6',
        backgroundColor: 'rgba(155, 89, 182, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#9B59B6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14 },
        bodyFont: { size: 13 },
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
      },
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45, minRotation: 45 }
      }
    }
  };

  if (topUsersError) {
    return (
      <div className="analytics-tab">
        <div className="error-message">
          <h4>Error Loading User Data</h4>
          <p>{topUsersError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-tab users-tab">
      {/* View Toggle */}
      <div className="view-toggle">
        <button
          className={`toggle-btn ${viewMode === 'overview' ? 'active' : ''}`}
          onClick={() => setViewMode('overview')}
        >
          Overview
        </button>
        <button
          className={`toggle-btn ${viewMode === 'users' ? 'active' : ''}`}
          onClick={() => setViewMode('users')}
        >
          Users
        </button>
      </div>

      {viewMode === 'overview' ? (
        <div className="overview-content">
          {/* Summary Cards */}
          {dailyData && (
            <div className="summary-cards">
              <div className="summary-card">
                <span className="card-value">{dailyData.totals.total_sessions.toLocaleString()}</span>
                <span className="card-label">Total Sessions</span>
              </div>
              <div className="summary-card">
                <span className="card-value">{dailyData.totals.total_unique_users.toLocaleString()}</span>
                <span className="card-label">Unique Users</span>
              </div>
              <div className="summary-card">
                <span className="card-value">{dailyData.totals.total_duration_minutes.toFixed(1)} min</span>
                <span className="card-label">Total Time</span>
              </div>
              <div className="summary-card">
                <span className="card-value">{dailyData.totals.avg_duration_minutes.toFixed(1)} min</span>
                <span className="card-label">Avg Session</span>
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="charts-grid">
            <div className="chart-container">
              <div className="chart-header">
                <h3>Daily Unique Users</h3>
              </div>
              {dailyLoading ? (
                <div className="chart-skeleton" style={{ height: '300px' }}></div>
              ) : uniqueUsersChartData ? (
                <div className="chart-wrapper" style={{ height: '300px' }}>
                  <Line data={uniqueUsersChartData} options={chartOptions} />
                </div>
              ) : (
                <p className="no-data">No user data available</p>
              )}
            </div>

            <div className="chart-container">
              <div className="chart-header">
                <h3>Avg Session Duration</h3>
              </div>
              {dailyLoading ? (
                <div className="chart-skeleton" style={{ height: '300px' }}></div>
              ) : durationChartData ? (
                <div className="chart-wrapper" style={{ height: '300px' }}>
                  <Line data={durationChartData} options={chartOptions} />
                </div>
              ) : (
                <p className="no-data">No duration data available</p>
              )}
            </div>

            <div className="chart-container">
              <div className="chart-header">
                <h3>Daily Sessions</h3>
              </div>
              {dailyLoading ? (
                <div className="chart-skeleton" style={{ height: '300px' }}></div>
              ) : sessionChartData ? (
                <div className="chart-wrapper" style={{ height: '300px' }}>
                  <Line data={sessionChartData} options={chartOptions} />
                </div>
              ) : (
                <p className="no-data">No session data available</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <TopUsersTable
          data={topUsersData || { users: [], total_users: 0, returned_count: 0 }}
          loading={topUsersLoading}
          onUserClick={handleUserClick}
        />
      )}

      {showModal && selectedUserId && (
        <UserDetailModal
          userId={selectedUserId}
          data={userDetailData}
          loading={userDetailLoading}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};
